import { useEffect, useRef, useState } from 'react';
import { X, Loader2, User, Mail, Phone, Lock } from 'lucide-react';
import { ClientAPI } from '../../lib/api/clients';
import { MessagingAPI } from '../../lib/api/messaging';
import { useUserStore } from '../../stores/userStore';
import { sendPropertyInquiryEmail } from '../../lib/api/email';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';

interface ContactAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentName: string;
  agentEmail: string;
  propertyTitle: string;
  propertyLink?: string;
  defaultMessage?: string;
  propertyId?: string;
  agentId?: string | undefined;
  inquiryType?: 'contact' | 'message' | 'inspection';
}

export default function ContactAgentModal({
  isOpen,
  onClose,
  agentName,
  agentEmail,
  propertyTitle,
  propertyLink,
  defaultMessage,
  propertyId,
  agentId,
  inquiryType = 'contact',
}: ContactAgentModalProps) {
  const { user } = useUserStore();
  
  const [message, setMessage] = useState<string>(
    defaultMessage || `Hi ${agentName},\n\nI am interested in "${propertyTitle}". Could you share more details and available viewing times?\n\nProperty: ${propertyLink || window.location.href}`
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.fullName || '',
    email: user?.email || '',
    phone: '',
  });

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.fullName || prev.name,
        email: user.email || prev.email,
      }));
    }
  }, [user]);

  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => dialogRef.current?.focus(), 0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  // Show authentication required message if user is not logged in
  if (!user) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={handleBackdropClick}
        aria-modal="true"
        role="dialog"
      >
        <div className="w-full max-w-lg rounded-lg bg-white shadow-xl p-6 text-center">
          <div className="text-blue-600 mb-4">
            <Lock className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold mb-2">
            Authentication Required
          </h3>
          <p className="text-neutral-600 mb-2">
            To send messages and inquiries, you need to be logged in to your account.
          </p>
          <p className="text-neutral-500 text-sm mb-6">
            Creating an account also lets you apply for properties, save favorites, and schedule viewings.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signin"
              className="btn-primary"
              onClick={onClose}
            >
              Sign In
            </Link>
            <Link
              to="/register-enhanced"
              className="btn-outline"
              onClick={onClose}
            >
              Create Account
            </Link>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-700 mt-4 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!propertyId) {
      console.error('Missing propertyId');
      return;
    }

    // Debug logging
    console.log('🔍 ContactAgentModal: Sending email with:', {
      agentName,
      agentEmail,
      propertyTitle,
      inquiryType
    });

    setIsSubmitting(true);
    try {
      // First, send email via Mailgun
      const emailResult = await sendPropertyInquiryEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: message,
        propertyTitle: propertyTitle,
        propertyLink: propertyLink,
        inquiryType: inquiryType,
        agentName: agentName,
        agentEmail: agentEmail,
      });

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to send email');
      }

      // Get property owner information to create proper conversation
      let propertyOwnerId = agentId;
      let isPropertyOwnerAgent = false;
      
      console.log('🔍 ContactAgentModal: Starting conversation creation process');
      console.log('🔍 ContactAgentModal: Initial agentId:', agentId);
      console.log('🔍 ContactAgentModal: propertyId:', propertyId);
      
      // Always fetch property information to get the owner_id
      console.log('🔍 ContactAgentModal: Fetching property information...');
      const { data: property, error: propError } = await supabase
        .from('properties')
        .select('owner_id, title')
        .eq('id', propertyId)
        .single();
      
      if (propError) {
        console.warn('⚠️ ContactAgentModal: Failed to get property info:', propError);
        throw new Error('Failed to get property information');
      }
      
      if (!property?.owner_id) {
        console.warn('⚠️ ContactAgentModal: Property has no owner_id');
        throw new Error('Property owner information not found');
      }
      
      // Use the property owner ID as the primary identifier
      propertyOwnerId = property.owner_id;
      console.log('🔍 ContactAgentModal: Property owner ID:', propertyOwnerId);
      
      // Check if the property owner is actually an agent
      if (propertyOwnerId) {
        console.log('🔍 ContactAgentModal: Checking if property owner is an agent...');
        try {
          const { data: agentCheck, error: agentError } = await supabase
            .from('agents')
            .select('id, agency_name')
            .eq('user_id', propertyOwnerId)
            .eq('is_active', true)
            .single();
          
          if (!agentError && agentCheck) {
            isPropertyOwnerAgent = true;
            console.log('✅ ContactAgentModal: Property owner is an agent:', agentCheck.id);
          } else {
            console.log('ℹ️ ContactAgentModal: Property owner is not an agent (regular landlord)');
          }
        } catch {
          console.log('ℹ️ ContactAgentModal: Property owner is not an agent, will create conversation without agent_id');
        }
      }

      console.log('🔍 ContactAgentModal: Final propertyOwnerId:', propertyOwnerId);
      console.log('🔍 ContactAgentModal: isPropertyOwnerAgent:', isPropertyOwnerAgent);

      // Always try to create conversation if we have a valid property owner ID
      if (propertyOwnerId && propertyOwnerId.trim() !== '') {
        try {
          console.log('🔍 ContactAgentModal: Creating conversation for property owner:', propertyOwnerId);
          
          let clientId: string | undefined;
          
          // Only create client if the property owner is an agent
          if (isPropertyOwnerAgent) {
            console.log('🔍 ContactAgentModal: Creating client since property owner is an agent...');
            try {
              const client = await ClientAPI.createFromInquiry(
                formData.name,
                formData.email,
                formData.phone,
                message,
                propertyId,
                propertyOwnerId
              );
              clientId = client.id;
              console.log('✅ ContactAgentModal: Client created successfully:', clientId);
            } catch (clientError) {
              console.warn('⚠️ ContactAgentModal: Failed to create client, but will continue with conversation:', clientError);
            }
          } else {
            console.log('🔍 ContactAgentModal: Skipping client creation since property owner is not an agent');
          }

          // Create conversation with property owner (landlord or agent)
          console.log('🔍 ContactAgentModal: Creating conversation...');
          const conversation = await MessagingAPI.createConversation({
            property_id: propertyId,
            agent_id: isPropertyOwnerAgent ? propertyOwnerId : undefined, // Only set if owner is an agent
            client_id: clientId, // Only set if client was created
            type: 'property' // Property inquiry conversation
          });
          console.log('✅ ContactAgentModal: Conversation created successfully:', conversation.id);

          // Send initial message using authenticated user's ID
          console.log('🔍 ContactAgentModal: Sending initial message...');
          console.log('🔍 ContactAgentModal: Using authenticated user ID:', user!.id);
          
          await MessagingAPI.sendMessage({
            conversation_id: conversation.id,
            sender_id: user!.id, // Use authenticated user's ID
            content: message
          });
          console.log('✅ ContactAgentModal: Initial message sent successfully');

          console.log('✅ ContactAgentModal: Conversation created successfully for property owner:', propertyOwnerId, 'isAgent:', isPropertyOwnerAgent);
        } catch (conversationError) {
          console.error('🚨 ContactAgentModal: Failed to create conversation:', conversationError);
          console.warn('⚠️ ContactAgentModal: Email was sent, but conversation creation failed');
          // Continue with success since email was sent
        }
      } else {
        console.error('🚨 ContactAgentModal: No valid property owner ID available');
        console.log('ℹ️ ContactAgentModal: Email was sent, but no conversation could be created');
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Check if the error is due to missing agent email
      if (error instanceof Error && error.message.includes('Missing agent email address')) {
        alert('Unable to send message: No contact email available for this property. Please try contacting the property owner through other means.');
      } else {
        // Fallback to mailto link if everything fails and we have a valid email
        if (agentEmail && agentEmail.trim() !== '') {
          const subject = encodeURIComponent(`Inquiry about ${propertyTitle}`);
          const body = encodeURIComponent(message);
          const mailtoUrl = `mailto:${agentEmail}?subject=${subject}&body=${body}`;
          window.location.href = mailtoUrl;
        } else {
          alert('Unable to send message: No contact email available for this property. Please try contacting the property owner through other means.');
        }
      }
      
      setTimeout(onClose, 400);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (showSuccess) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={handleBackdropClick}
        aria-modal="true"
        role="dialog"
      >
        <div className="w-full max-w-lg rounded-lg bg-white shadow-xl p-6 text-center">
          <div className="text-green-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">
            {inquiryType === 'inspection' ? 'Inspection Request Sent!' : 'Message Sent Successfully!'}
          </h3>
          <p className="text-neutral-600">
            {inquiryType === 'inspection' 
              ? `Your inspection request for "${propertyTitle}" has been sent to ${agentName}. They will contact you with available viewing times.`
              : `Your message has been sent to ${agentName}. They will get back to you soon.`
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="w-full max-w-lg rounded-lg bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-neutral-200 p-4">
          <h3 className="text-lg font-semibold">
            {inquiryType === 'inspection' 
              ? `Request Inspection - ${agentName}`
              : inquiryType === 'message'
              ? `Send Message - ${agentName}`
              : `Contact ${agentName}`
            }
          </h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSend} className="p-4">
          {/* Contact Information */}
          <div className="mb-4">
            <p className="text-sm text-neutral-600 mb-3">
              Your contact information is pre-filled from your account. You can update your phone number below.
            </p>
            <div className="space-y-3">
            <div>
              <label htmlFor="name" className="mb-1 block text-sm font-medium text-neutral-700">
                <User className="h-4 w-4 inline mr-1" />
                Full Name *
              </label>
              <input
                id="name"
                type="text"
                required
                value={formData.name}
                readOnly
                className="form-input w-full bg-neutral-50"
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-neutral-700">
                <Mail className="h-4 w-4 inline mr-1" />
                Email *
              </label>
              <input
                id="email"
                type="email"
                required
                value={formData.email}
                readOnly
                className="form-input w-full bg-neutral-50"
                placeholder="Enter your email address"
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="mb-1 block text-sm font-medium text-neutral-700">
                <Phone className="h-4 w-4 inline mr-1" />
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="form-input w-full"
                placeholder="Enter your phone number (optional)"
              />
            </div>
          </div>
          </div>

          {/* Message */}
          <div className="mb-4">
            <label htmlFor="message" className="mb-1 block text-sm font-medium text-neutral-700">
              {inquiryType === 'inspection' ? 'Inspection Request Details *' : 'Message *'}
            </label>
            <textarea
              id="message"
              rows={6}
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="form-textarea w-full"
              placeholder={
                inquiryType === 'inspection' 
                  ? "Tell the agent about your inspection request and preferred dates..."
                  : inquiryType === 'message'
                  ? "Tell the agent about your interest in this property..."
                  : "Tell the agent about your inquiry..."
              }
            />
            <p className="mt-1 text-xs text-neutral-500">
              {inquiryType === 'inspection' 
                ? `Your inspection request will be sent directly to ${agentName} via email and saved in their client management system.`
                : `This message will be sent directly to ${agentName} via email and saved in their client management system.`
              }
            </p>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-outline px-4">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary px-4">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 
                inquiryType === 'inspection' ? 'Request Inspection' :
                inquiryType === 'message' ? 'Send Message' : 'Contact Agent'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


